import { subject } from '@casl/ability'
import { IncidentCaseView } from '@features/logs/components/incidents/IncidentCaseView'
import useLog from '@features/logs/hooks/useLog'
import { type Home, IncidentStatus, UserRole } from '@lmc/ts-common'
import {
  Avatar,
  Card,
  Edit,
  EmptyState,
  Heading,
  NoDataIcon,
  PageHeader,
  Restore,
  Tag,
  Trash
} from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import Description from 'components/generic/LogTimelineRow/Description'
import dayjs from 'dayjs'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import usePdfExport from 'hooks/usePdfExport'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import GeoLocation from '../../../../components/GeoLocation/GeoLocation'
import { getPdfTitle, tagColorMap } from '../../utils/incidentUtils'
import AttachmentsRow from './AttachmentsRow'
import {
  HeaderChildrenContainer,
  IconContainer,
  LogTitle,
  TitleContainer,
  Wrapper
} from './commonStyledComponents'
import CloseIncidentModal from './modals/CloseIncidentModal'
import RemoveIncidentModal from './modals/RemoveIncidentModal'
import ReopenIncidentModal from './modals/ReopenIncidentModal'
import RestoreIncidentModal from './modals/RestoreIncidentModal'
import ReviewLogDropdownButton from './ReviewLogDropdownButton'

type IncidentLogScreenProps = {
  testId?: string
}

const IncidentLogScreen = ({
  testId = 'incident-detail'
}: IncidentLogScreenProps = {}) => {
  const navigate = useNavigate()
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { id: incidentLogId } = useParams()
  const { incidentReport, isIncidentReportLoading, refetch } =
    useIncidentReportByLog({
      incidentLogId
    })
  const [isClosingIncident, setIsClosingIncident] = useState(false)
  const [isReopeningIncident, setIsReopeningIncident] = useState(false)
  const [isRestoringIncident, setIsRestoringIncident] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [hasDownloadStarted, setHasDownloadStarted] = useState(false)
  const { removeLog, isRemoveLoading } = useLog({
    logId: incidentLogId,
    onRemoveSuccess: () => {
      setIsRemoveModalOpen(false)
      showToast({
        title: t(incidentReportsConstants.LOG_REMOVED)
      })
      refetch()
    }
  })
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const pdfContent = useRef<HTMLInputElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const handleDownloadPdf = usePdfExport(
    pdfContent,
    incidentReport
      ? getPdfTitle(incidentReport, t(incidentReportsConstants.INCIDENT_CASE))
      : t(incidentReportsConstants.INCIDENT_CASE),
    setIsDownloading
  )

  const gpsLocationEnabled = home.features?.geoLocation?.enabled
  const hasLocationsEnabled = home?.features?.hasHomeLocations?.enabled

  const location = useLocation()
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const rootPage = pathSegments.slice(0, -1).join('/')

  const logsPath = `/${rootPage}/${incidentLogId}`

  const onCreateCaseClick = () => {
    navigate(`${logsPath}/create-case`)
  }

  const onCloseIncidentClick = () => {
    setIsClosingIncident(true)
  }

  const onDownloadPDFClick = () => {
    setHasDownloadStarted(true)
  }

  const handleRemoveLog = async () => {
    try {
      await removeLog()
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (hasDownloadStarted) {
      handleDownloadPdf()
      setHasDownloadStarted(false)
    }
  }, [hasDownloadStarted])

  const hasPermissionToCreateCase = useAbilityAccess([
    {
      i: 'update',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  const hasPermissionToEditCase = useAbilityAccess([
    {
      i: 'update',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  // CM-4059: closing an incident is a Delete-tier action on the backend
  // (closeIncidentReport requires Action.Delete on Incident), so gate on `delete`
  // — Senior Carers have update-but-not-delete and must not see an enabled Close.
  const hasPermissionToCloseCase = useAbilityAccess([
    {
      i: 'delete',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  const hasPermissionToRemoveLog = useAbilityAccess([
    {
      i: 'delete',
      this: subject('Incident', {
        log: { item: incidentReport?.log.item?.id }
      })
    }
  ])

  if (isIncidentReportLoading)
    return <Spinner data-testid={`${testId}-spinner`} />
  if (!incidentReport || Object.keys(incidentReport).length <= 0)
    return (
      <EmptyState
        data-testid={`${testId}-empty-state`}
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )

  // hasCase is true if the incident report has a case opened and if isDraft is falsy
  // that means that the case was opened by a user and not pre-populated
  const hasCase = incidentReport.case && !incidentReport.case.isDraft

  const isClosed = incidentReport.status === IncidentStatus.closed

  const isDeleted = incidentReport.log.deleted

  const showLocation = incidentReport.log?.location && hasLocationsEnabled

  const serviceUserPicture = incidentReport.serviceUser
    ?.picture as unknown as string

  const attachmentsFromCase =
    incidentReport.attachments?.filter((url) => url) || []

  const attachmentsFromLogs = [
    incidentReport?.log?.picture1,
    incidentReport?.log?.picture2,
    incidentReport?.log?.picture3
  ]

  const videoFile = incidentReport?.log?.videoFile
  const videoThumbnail = incidentReport?.log?.videoThumbnail

  const regularAttachments = [
    ...attachmentsFromCase,
    ...attachmentsFromLogs
  ].filter(Boolean) as string[]

  const attachments = [
    ...regularAttachments,
    ...(videoFile
      ? [
          {
            url: videoFile,
            thumbnail: videoThumbnail,
            isVideo: true
          }
        ]
      : [])
  ]

  return (
    <>
      <PageHeader
        testId={`${testId}`}
        sticky
        title={
          <TitleContainer data-testid={`${testId}-title-container`}>
            <IconContainer
              data-testid={`${testId}-icon`}
              src={
                (incidentReport.log.item?.icon ||
                  incidentReport.log.category?.icon) as unknown as string
              }
              $color={incidentReport.log.category.color}
              alt="icon"
            />
            {incidentReport.log.title}
            {isDeleted ? (
              <Tag data-testid={`${testId}-removed-tag`}>
                {t(incidentReportsConstants.REMOVED)}
              </Tag>
            ) : (
              <Tag
                data-testid={`${testId}-status-tag`}
                color={tagColorMap[incidentReport.status]}
              >
                {t(incidentReportsConstants[incidentReport.status])}
              </Tag>
            )}
          </TitleContainer>
        }
        onBack={() => navigate('../', { relative: 'path' })}
      >
        <HeaderChildrenContainer data-testid={`${testId}-header-actions`}>
          {hasCase && !isClosed && (
            <Button
              data-testid={`${testId}-download-pdf-button`}
              onClick={onDownloadPDFClick}
              isLoading={isDownloading}
            >
              {t(incidentReportsConstants.DOWNLOAD_PDF)}
            </Button>
          )}
          <Link
            data-testid={`${testId}-edit-link`}
            to={hasCase ? `${logsPath}/edit-case` : `${logsPath}/edit`}
          >
            <Button
              data-testid={`${testId}-edit-button`}
              disabled={incidentReport.log.deleted || isClosed}
              variant={hasCase ? 'primary' : 'default'}
              disabledByPermission={!hasPermissionToEditCase}
            >
              <Edit />
              {t(incidentReportsConstants.EDIT)}
            </Button>
          </Link>
          {isClosed && (
            <Button
              data-testid={`${testId}-reopen-button`}
              onClick={() => {
                setIsReopeningIncident(true)
              }}
            >
              <Restore /> {t(incidentReportsConstants.REOPEN_INCIDENT)}
            </Button>
          )}
          {hasCase && isClosed && (
            <Button
              data-testid={`${testId}-download-pdf-button-closed`}
              onClick={onDownloadPDFClick}
              isLoading={isDownloading}
            >
              {t(incidentReportsConstants.DOWNLOAD_PDF)}
            </Button>
          )}
          {/* CM-4663: Remove lives here rather than on an edit screen because
              Edit is disabled once an incident is closed — so the edit screens
              are unreachable in exactly the state where removal is allowed.
              Removal is deliberately blocked while a case is still open. */}
          {isClosed && !isDeleted && (
            <Button
              data-testid={`${testId}-remove-button`}
              variant="danger"
              onClick={() => setIsRemoveModalOpen(true)}
              disabledByPermission={!hasPermissionToRemoveLog}
            >
              <Trash />
              {t(incidentReportsConstants.REMOVE)}
            </Button>
          )}
          {isDeleted && (
            <Button
              data-testid={`${testId}-restore-button`}
              onClick={() => {
                setIsRestoringIncident(true)
              }}
            >
              <Restore /> {t(incidentReportsConstants.RESTORE)}
            </Button>
          )}
          {!isClosed && !hasCase && !isDeleted && (
            <ReviewLogDropdownButton
              data-testid={`${testId}-review-dropdown`}
              hasPermissionToCloseCase={hasPermissionToCloseCase}
              hasPermissionToCreateCase={hasPermissionToCreateCase}
              onCloseIncidentClick={onCloseIncidentClick}
              onCreateCaseClick={onCreateCaseClick}
            />
          )}
        </HeaderChildrenContainer>
      </PageHeader>
      <Wrapper data-testid={`${testId}-wrapper`} ref={pdfContent}>
        <LogCard data-testid={`${testId}-log-card`}>
          <LogTitle data-testid={`${testId}-log-title`} level={3}>
            {t(incidentReportsConstants.LOG)}
          </LogTitle>
          <Divider />
          <NameSection data-testid={`${testId}-name-section`}>
            <Link
              data-testid={`${testId}-service-user-link`}
              to={`/residents/${incidentReport.serviceUser?.id}/profile`}
            >
              <Avatar
                data-testid={`${testId}-service-user-avatar`}
                size={64}
                url={serviceUserPicture}
              />
            </Link>
            <ServiceUserName
              data-testid={`${testId}-service-user-name`}
              level={4}
              weight="bold"
            >
              {incidentReport.serviceUser.name}
            </ServiceUserName>
          </NameSection>
          <Divider />
          {(showLocation || incidentReport.log.witnessedBy?.name) && (
            <FlexRow data-testid={`${testId}-location-witnessed-row`}>
              {showLocation && (
                <DescriptionSection data-testid={`${testId}-location-section`}>
                  <DescriptionSectionTitle level={2}>
                    {t(incidentReportsConstants.LOCATION)}
                  </DescriptionSectionTitle>
                  <DescriptionSectionValue
                    data-testid={`${testId}-location-value`}
                  >
                    {incidentReport.log.location}
                  </DescriptionSectionValue>
                </DescriptionSection>
              )}
              {incidentReport.log.witnessedBy?.name && (
                <DescriptionSection
                  data-testid={`${testId}-witnessed-by-section`}
                >
                  <DescriptionSectionTitle level={2}>
                    {t(incidentReportsConstants.WITNESSED_BY)}
                  </DescriptionSectionTitle>
                  <DescriptionSectionValue
                    data-testid={`${testId}-witnessed-by-value`}
                  >
                    {incidentReport.log.witnessedBy.name}
                  </DescriptionSectionValue>
                </DescriptionSection>
              )}
            </FlexRow>
          )}
          <DescriptionSection
            data-testid={`${testId}-description-section`}
            $wide
          >
            <DescriptionSectionTitle level={2}>
              {t(incidentReportsConstants.DESCRIPTION)}
            </DescriptionSectionTitle>
            <Description
              data-testid={`${testId}-description`}
              description={incidentReport.log.description}
              downloadStarted={hasDownloadStarted}
            />
          </DescriptionSection>
          {attachments?.length > 0 && (
            <DescriptionSection
              data-testid={`${testId}-attachments-section`}
              $wide
            >
              <DescriptionSectionTitle level={2}>
                {t(incidentReportsConstants.ATTACHMENTS)}
              </DescriptionSectionTitle>
              <AttachmentsContainer>
                <AttachmentsRow
                  data-testid={`${testId}-attachments`}
                  attachments={attachments}
                  testId={`${testId}-attachments`}
                />
              </AttachmentsContainer>
            </DescriptionSection>
          )}

          <FlexRow data-testid={`${testId}-metadata-row`}>
            <DescriptionSection data-testid={`${testId}-recorded-by-section`}>
              <DescriptionSectionTitle level={2}>
                {t(incidentReportsConstants.RECORDED_BY)}
              </DescriptionSectionTitle>
              <DescriptionSectionValue
                data-testid={`${testId}-recorded-by-value`}
              >
                {incidentReport.log.loggedBy?.name}
              </DescriptionSectionValue>
            </DescriptionSection>
            <DescriptionSection data-testid={`${testId}-time-logged-section`}>
              <DescriptionSectionTitle level={2}>
                {t(incidentReportsConstants.TIME_LOGGED)}
              </DescriptionSectionTitle>
              <DescriptionSectionValue
                data-testid={`${testId}-time-logged-value`}
              >
                {dayjs(incidentReport.log.timeLogged).format(
                  'DD-MM-YYYY HH:mm'
                )}
              </DescriptionSectionValue>
            </DescriptionSection>
            {incidentReport.closedBy && incidentReport.status === 'closed' && (
              <DescriptionSection data-testid={`${testId}-closed-by-section`}>
                <DescriptionSectionTitle level={2}>
                  {t(incidentReportsConstants.CLOSED_BY)}
                </DescriptionSectionTitle>
                <DescriptionSectionValue
                  data-testid={`${testId}-closed-by-value`}
                >
                  {incidentReport.closedBy.name}
                </DescriptionSectionValue>
              </DescriptionSection>
            )}
            {incidentReport.closedBy &&
              incidentReport.status === 'closed' &&
              incidentReport?.closeComment && (
                <DescriptionSection
                  data-testid={`${testId}-close-comment-section`}
                >
                  <DescriptionSectionTitle level={2}>
                    {t(incidentReportsConstants.CLOSE_COMMENT)}
                  </DescriptionSectionTitle>
                  <DescriptionSectionValue
                    data-testid={`${testId}-close-comment-value`}
                  >
                    {incidentReport.closeComment}
                  </DescriptionSectionValue>
                </DescriptionSection>
              )}
          </FlexRow>
          {incidentReport.log.goals &&
            incidentReport.log?.goals?.length > 0 && (
              <FlexRow data-testid={`${testId}-goals-row`}>
                <DescriptionSection data-testid={`${testId}-goals-section`}>
                  <DescriptionSectionTitle level={2}>
                    {t(incidentReportsConstants.LINKED_GOALS)}
                  </DescriptionSectionTitle>
                  <DescriptionSectionValue
                    data-testid={`${testId}-goals-value`}
                  >
                    {incidentReport?.log?.goals.map((goal) => goal.title)}
                  </DescriptionSectionValue>
                </DescriptionSection>
              </FlexRow>
            )}
          <Divider />

          <DescriptionSection data-testid={`${testId}-gps-section`} $wide>
            <DescriptionSectionTitle level={2}>
              {t(incidentReportsConstants.GPS_LOCATION)}
            </DescriptionSectionTitle>
            {gpsLocationEnabled && (
              <GeoLocation
                data-testid={`${testId}-gps-location`}
                longitude={incidentReport.log.longitude}
                latitude={incidentReport.log.latitude}
                accuracy={incidentReport.log.accuracy}
              />
            )}
          </DescriptionSection>
        </LogCard>
        {hasCase && (
          <IncidentCaseView
            testId={`${testId}-case-view`}
            incidentReport={incidentReport}
            severities={home.incidentSeverity}
          />
        )}
        <CloseIncidentModal
          data-testid={`${testId}-close-modal`}
          open={isClosingIncident}
          setIsClosingIncident={setIsClosingIncident}
          incidentReport={incidentReport}
          onSuccess={refetch}
        />
        <ReopenIncidentModal
          data-testid={`${testId}-reopen-modal`}
          open={isReopeningIncident}
          setIsReopeningIncident={setIsReopeningIncident}
          incidentReport={incidentReport}
          refetch={refetch}
        />
        <RemoveIncidentModal
          data-testid={`${testId}-remove-modal`}
          open={isRemoveModalOpen}
          onConfirm={handleRemoveLog}
          onCancel={() => setIsRemoveModalOpen(false)}
          isRemoveLoading={isRemoveLoading}
        />
        <RestoreIncidentModal
          data-testid={`${testId}-restore-modal`}
          open={isRestoringIncident}
          setIsRestoringIncident={setIsRestoringIncident}
          logId={incidentReport.log.id}
          refetch={refetch}
        />
      </Wrapper>
    </>
  )
}

const ServiceUserName = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const LogCard = styled(Card)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const NameSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const FlexRow = styled.div`
  display: flex;
`

const DescriptionSection = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${(props) => (props?.$wide ? '100%' : '230px')};
`

const DescriptionSectionTitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const DescriptionSectionValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const AttachmentsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`

export default IncidentLogScreen
